import { computed, ref } from 'vue'

/**
 * Centre de notifications unifié.
 *
 * Refonte « moins de bruit » : chaque notification porte une *catégorie* dont on dérive un *tier*.
 *   - rare     → évènements précieux (shiny, Pokédex complété, titre légendaire). Toast marquant + journal.
 *   - routine  → progression de fond (évolutions, niveaux, succès, pension, auto-farm). Journal seulement.
 *   - feedback → résultat direct d'une action du joueur (réclamation, achat, erreur). Toast éphémère.
 *
 * « Ne pas déranger » (DND) = silence total : plus aucun toast ; tout part dans le journal (rien perdu).
 * Une catégorie désactivée n'apparaît ni en toast ni dans le journal.
 */

export type NotifTier = 'rare' | 'routine' | 'feedback'
export type NotifType = 'info' | 'success' | 'warning' | 'error'

/** Catégories pilotables par le joueur dans le profil. */
export type NotifCategory =
  | 'shiny' // rare
  | 'milestone' // rare : Pokédex gen/master, titre légendaire
  | 'evolution' // routine
  | 'progression' // routine : niveau, badge, succès, titre, jalons, déblocages
  | 'daycare' // routine : œufs prêts, éclosions normales
  | 'autofarm' // routine : rotations auto-maîtrise
  | 'action' // feedback : réclamations, achats, erreurs, divers

const CATEGORY_TIER: Record<NotifCategory, NotifTier> = {
  shiny: 'rare',
  milestone: 'rare',
  evolution: 'routine',
  progression: 'routine',
  daycare: 'routine',
  autofarm: 'routine',
  action: 'feedback',
}

export interface Toast {
  id: number
  message: string
  icon?: string
  type?: NotifType
  duration?: number
  tier?: NotifTier
}

export interface JournalEntry {
  id: number
  ts: number
  message: string
  icon: string
  type: NotifType
  tier: NotifTier
  category: NotifCategory
  /** Nombre d'occurrences fusionnées (coalescing). */
  count: number
  read: boolean
}

export interface NotifPrefs {
  /** Ne pas déranger : aucun toast (le journal continue de tout enregistrer). */
  dnd: boolean
  /** Catégories activées (true = visible). Absent ⇒ activé par défaut. */
  categories: Partial<Record<NotifCategory, boolean>>
}

const PREFS_KEY = 'poke-idle-notif-center-prefs'
const JOURNAL_KEY = 'poke-idle-notif-journal'
const JOURNAL_MAX = 60
/** Fenêtre de fusion des routines de même clé dans le journal. */
const COALESCE_WINDOW_MS = 8_000

// ── État singleton (module-level), comme l'ancien useToast ──────────────────
const toasts = ref<Toast[]>([])
const journal = ref<JournalEntry[]>([])
let nextId = 0

function loadPrefs(): NotifPrefs {
  if (typeof localStorage === 'undefined') return { dnd: false, categories: {} }
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<NotifPrefs>
      return { dnd: parsed.dnd ?? false, categories: parsed.categories ?? {} }
    }
  } catch {
    /* ignore */
  }
  return { dnd: false, categories: {} }
}

const prefs = ref<NotifPrefs>(loadPrefs())

function persistPrefs() {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs.value))
  } catch {
    /* ignore */
  }
}

function loadJournal() {
  if (typeof localStorage === 'undefined') return
  try {
    const raw = localStorage.getItem(JOURNAL_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as JournalEntry[]
    if (Array.isArray(parsed)) {
      journal.value = parsed.slice(-JOURNAL_MAX)
      nextId = Math.max(nextId, ...journal.value.map((e) => e.id + 1), 0)
    }
  } catch {
    /* ignore */
  }
}
let journalLoaded = false
function ensureJournalLoaded() {
  if (journalLoaded) return
  journalLoaded = true
  loadJournal()
}

function persistJournal() {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(JOURNAL_KEY, JSON.stringify(journal.value.slice(-JOURNAL_MAX)))
  } catch {
    /* ignore */
  }
}

function isCategoryEnabled(category: NotifCategory): boolean {
  return prefs.value.categories[category] ?? true
}

function removeToast(id: number) {
  const idx = toasts.value.findIndex((t) => t.id === id)
  if (idx !== -1) toasts.value.splice(idx, 1)
}

function pushToast(message: string, icon: string, type: NotifType, duration: number, tier: NotifTier) {
  const id = nextId++
  toasts.value.push({ id, message, icon, type, duration, tier })
  setTimeout(() => removeToast(id), duration)
}

function pushJournal(
  message: string,
  icon: string,
  type: NotifType,
  tier: NotifTier,
  category: NotifCategory,
  coalesceKey?: string,
  countDelta = 1,
) {
  ensureJournalLoaded()
  const now = Date.now()
  const delta = Math.max(1, Math.floor(Number(countDelta) || 1))
  // Coalescing : même clé + même catégorie dans la fenêtre ⇒ on incrémente le compteur.
  if (coalesceKey) {
    const last = journal.value[journal.value.length - 1]
    if (
      last &&
      last.category === category &&
      (last as JournalEntry & { _ck?: string })._ck === coalesceKey &&
      now - last.ts < COALESCE_WINDOW_MS
    ) {
      last.count += delta
      last.ts = now
      last.read = false
      last.message = message
      persistJournal()
      return
    }
  }
  const entry: JournalEntry & { _ck?: string } = {
    id: nextId++,
    ts: now,
    message,
    icon,
    type,
    tier,
    category,
    count: delta,
    read: false,
    _ck: coalesceKey,
  }
  journal.value.push(entry)
  if (journal.value.length > JOURNAL_MAX) {
    journal.value.splice(0, journal.value.length - JOURNAL_MAX)
  }
  persistJournal()
}

export interface NotifyOptions {
  icon?: string
  type?: NotifType
  duration?: number
  /** Clé de fusion dans le journal (ex. 'evolution'); les routines identiques se cumulent. */
  coalesceKey?: string
  /** Incrémente le compteur journal de N (défaut 1) — utile si plusieurs shinys arrivent d’un coup. */
  countDelta?: number
}

/**
 * Point d'entrée principal. Route le message selon sa catégorie, le tier dérivé et les préférences.
 */
function notify(category: NotifCategory, message: string, options: NotifyOptions = {}) {
  const tier = CATEGORY_TIER[category]
  const icon = options.icon ?? defaultIcon(category)
  const type = options.type ?? defaultType(tier)
  const duration = options.duration ?? defaultDuration(tier)

  // Catégorie coupée : rien, nulle part.
  if (!isCategoryEnabled(category)) return

  // Le journal enregistre les évènements persistants (rare + routine), pas le feedback éphémère.
  if (tier !== 'feedback') {
    pushJournal(
      message,
      icon,
      type,
      tier,
      category,
      options.coalesceKey ?? category,
      options.countDelta,
    )
  }

  // Toasts : routine = jamais (journal seulement) ; rare + feedback = visible sauf DND.
  if (tier === 'routine') return
  if (prefs.value.dnd) return
  pushToast(message, icon, type, duration, tier)
}

function defaultIcon(category: NotifCategory): string {
  switch (category) {
    case 'shiny':
      return '✨'
    case 'milestone':
      return '🏆'
    case 'evolution':
      return '🔄'
    case 'daycare':
      return '🥚'
    case 'autofarm':
      return '⭐'
    case 'progression':
      return '⬆️'
    default:
      return '🔔'
  }
}
function defaultType(tier: NotifTier): NotifType {
  return tier === 'rare' ? 'warning' : tier === 'feedback' ? 'info' : 'success'
}
function defaultDuration(tier: NotifTier): number {
  return tier === 'rare' ? 7000 : 4000
}

// ── Journal : API publique ──────────────────────────────────────────────────
const unreadCount = computed(() => {
  ensureJournalLoaded()
  return journal.value.reduce((n, e) => n + (e.read ? 0 : 1), 0)
})

function markJournalRead() {
  ensureJournalLoaded()
  let changed = false
  for (const e of journal.value) {
    if (!e.read) {
      e.read = true
      changed = true
    }
  }
  if (changed) persistJournal()
}

function clearJournal() {
  journal.value = []
  persistJournal()
}

// ── Préférences : API publique ──────────────────────────────────────────────
function setDnd(value: boolean) {
  prefs.value.dnd = value
  persistPrefs()
}
function setCategoryEnabled(category: NotifCategory, value: boolean) {
  prefs.value.categories = { ...prefs.value.categories, [category]: value }
  persistPrefs()
}

export function useNotificationCenter() {
  ensureJournalLoaded()
  return {
    // toasts
    toasts,
    removeToast,
    // notify
    notify,
    // journal
    journal,
    unreadCount,
    markJournalRead,
    clearJournal,
    // prefs
    prefs,
    isCategoryEnabled,
    setDnd,
    setCategoryEnabled,
  }
}

/** Réservé à useToast (rétro-compat). */
export function _legacyAddToast(message: string, icon: string, type: NotifType, duration: number) {
  // Les anciens appels addToast() sont des feedbacks d'action : visibles sauf DND, non journalisés.
  if (prefs.value.dnd) return
  // Historique : beaucoup d'échecs passent ('❌', 'warning') faute de type dédié → vrai type erreur.
  const resolvedType: NotifType = type === 'warning' && icon === '❌' ? 'error' : type
  pushToast(message, icon, resolvedType, duration, 'feedback')
}
